import { CenterFlexRow, FlexRow } from "styles/layout";
import { convertMsToSecondsString, convertMsToTimeString } from "TimeHelper";
import {
  AceContainer,
  AceIcon,
  DnfContainer,
  PaddingRow,
  PickingTeamLogoContainer,
  PlayerCell,
  TeamTrackPointsCell,
  TeamTrackPointsCellContents,
  SetMatchTimeCell,
  TimeDifferenceCell,
  SetTrackName,
  TrackStatsCenterCell,
  TrackStatsTable,
  CenterText,
} from "./styles";
import { ACE_ICON_SRC, DNF_ICON_SRC } from "theme";
import {
  statTimesBest,
  statTimesDnfCount,
  statTimesMedian,
} from "stats/TimeStats";
import { nonUndefined, pushX, timeAscending } from "Utility";
import { DnfIcon } from "../styles";
import { TimeDifferenceText } from "styles/text";
import { getResults } from "model/Results";
import { JSX } from "react";
import { RosterOrgLogo } from "components/Logo/RosterOrgLogo";
import {
  selectRosterDisplayName,
  selectTournamentDriverName,
} from "selectors/Roster";
import { selectTrackNameFromTournamentId } from "selectors/Tournament";
import { useAppSelector } from "app/hooks";
import { TrackBasedMatchTrack } from "model/Match/Track/MatchTrack";
import { TrackBasedTrackRound } from "model/Match/Track/TrackRound";
import { statRoundsTeamAceCount } from "stats/RoundStats";
import { selectMatchRosterDriverIds } from "selectors/Match";

const ACE_ICON = <AceIcon src={ACE_ICON_SRC} />;
const DNF_ICON = <DnfIcon src={DNF_ICON_SRC} />;

type DriverTrackStats = {
  best: number | undefined;
  dnfCount: number;
  median: number | undefined;
};

interface Props {
  trackId: string;
  matchTrack: TrackBasedMatchTrack | undefined;
  pickingRosterId: string | undefined;
  rosterOneId: string;
  rosterTwoId: string;
  matchId: string;
  tournamentId: string;
}

export const H2HTrackBasedTrackStats = ({
  trackId,
  matchTrack,
  pickingRosterId,
  rosterOneId,
  rosterTwoId,
  matchId,
  tournamentId,
}: Props) => {
  const trackName = useAppSelector((state) =>
    selectTrackNameFromTournamentId(state, trackId, tournamentId)
  );

  const pickingOrgName = useAppSelector((state) =>
    !!pickingRosterId
      ? selectRosterDisplayName(state, pickingRosterId, tournamentId)
      : undefined
  );

  const rosterOneDriverIds = useAppSelector(
    (state) => selectMatchRosterDriverIds(state, matchId, rosterOneId) ?? []
  );

  const rosterTwoDriverIds = useAppSelector(
    (state) => selectMatchRosterDriverIds(state, matchId, rosterTwoId) ?? []
  );

  const driverIds = rosterOneDriverIds.concat(rosterTwoDriverIds);

  const driverStats: {
    [driverId: string]: DriverTrackStats;
  } = {};
  driverIds.forEach(
    (driverId) =>
      (driverStats[driverId] = getDriverStats(
        matchTrack?.rounds ?? [],
        driverId
      ))
  );
  const allDriverStats = Object.values(driverStats);
  const allBests = nonUndefined(allDriverStats.flatMap((s) => s.best)).filter(
    (b) => b > 0
  );
  const overallBest = allBests.length > 0 ? Math.min(...allBests) : undefined;
  const allMedians = nonUndefined(
    allDriverStats.flatMap((s) => s.median)
  ).filter((m) => m > 0);
  const bestMedian =
    allMedians.length > 0 ? Math.min(...allMedians) : undefined;
  const roundTimes = matchTrack?.rounds.flatMap((r) => r.times) ?? [];
  var overallMedian = statTimesMedian(roundTimes);
  if (!!overallMedian) {
    if (overallMedian < 0) {
      overallMedian = undefined;
    } else {
      overallMedian = Math.round(overallMedian);
    }
  }

  const filteredRosterOneDriverIds = rosterOneDriverIds.filter(
    (id) => driverStats[id].best !== undefined
  );
  const filteredRosterTwoDriverIds = rosterTwoDriverIds.filter(
    (id) => driverStats[id].best !== undefined
  );

  const isTeamOnePick = pickingRosterId === rosterOneId;
  const isTeamTwoPick = pickingRosterId === rosterTwoId;
  const isRandomPick = !isTeamOnePick && !isTeamTwoPick;
  const trackResults = !!matchTrack
    ? getResults(matchTrack.finalPositions, 1)
    : undefined;
  const showTrackStats = !!matchTrack && !!trackResults;

  const pickingTeamLogo = (
    <PickingTeamLogoContainer
      teamOnePick={!isRandomPick ? isTeamOnePick : undefined}
      played={showTrackStats}
    >
      {!!pickingRosterId && (
        <RosterOrgLogo
          rosterId={pickingRosterId}
          tournamentId={tournamentId}
          title={`Picked by ${pickingOrgName}`}
        />
      )}
    </PickingTeamLogoContainer>
  );

  const teamScores = showTrackStats ? matchTrack.scores : undefined;

  return (
    <TrackStatsTable>
      <tbody>
        <tr>
          <TeamTrackPointsCell
            colSpan={3}
            winner={!!trackResults && trackResults[rosterOneId] === "W"}
          >
            <TeamTrackPointsCellContents leftAlign={false}>
              <AceContainer>
                {showTrackStats &&
                  getAceIcons(matchTrack.rounds, rosterOneDriverIds)}
              </AceContainer>
              {teamScores ? teamScores[rosterOneId] : ""}
            </TeamTrackPointsCellContents>
          </TeamTrackPointsCell>
          <TrackStatsCenterCell>
            <CenterFlexRow>
              {isTeamOnePick && pickingTeamLogo}
              <SetTrackName
                teamOnePick={!isRandomPick ? isTeamOnePick : undefined}
                played={showTrackStats}
              >
                {trackName}
              </SetTrackName>
              {isTeamTwoPick && pickingTeamLogo}
            </CenterFlexRow>
          </TrackStatsCenterCell>

          <TeamTrackPointsCell
            colSpan={3}
            winner={!!trackResults && trackResults[rosterTwoId] === "W"}
          >
            <TeamTrackPointsCellContents leftAlign={true}>
              {teamScores ? teamScores[rosterTwoId] : ""}
              <AceContainer>
                {showTrackStats &&
                  getAceIcons(matchTrack.rounds, rosterTwoDriverIds)}
              </AceContainer>
            </TeamTrackPointsCellContents>
          </TeamTrackPointsCell>
        </tr>
        {showTrackStats && (
          <>
            {getBestRows(
              filteredRosterOneDriverIds,
              filteredRosterTwoDriverIds,
              driverStats,
              overallBest,
              tournamentId
            )}
            <PaddingRow />
            {getMedianRows(
              filteredRosterOneDriverIds,
              filteredRosterTwoDriverIds,
              driverStats,
              bestMedian,
              overallMedian,
              tournamentId
            )}
          </>
        )}
      </tbody>
    </TrackStatsTable>
  );
};

const getDriverStats = (
  rounds: TrackBasedTrackRound[],
  driverId: string
): DriverTrackStats => {
  const driverRounds = rounds.flatMap((r) =>
    r.times.filter((t) => t.driverId === driverId)
  );
  const median = statTimesMedian(driverRounds);
  const roundedMedian = median !== undefined ? Math.round(median) : undefined;
  const best = statTimesBest(driverRounds, true);
  const dnfCount = statTimesDnfCount(driverRounds);
  return { median: roundedMedian, best, dnfCount };
};

const getAceIcons = (
  rounds: TrackBasedTrackRound[],
  rosterDriverIds: string[]
) => {
  if (rosterDriverIds.length === 1) {
    return <></>;
  }

  const aceCount = statRoundsTeamAceCount(rounds, rosterDriverIds);
  const aceIcons: JSX.Element[] = [];
  pushX(ACE_ICON, aceCount, aceIcons);
  return <>{aceIcons}</>;
};

const getDnfIcons = (dnfCount: number) => {
  if (dnfCount > 3) {
    return (
      <>
        {DNF_ICON}
        <span style={{ paddingLeft: "1px" }}>x</span>
        {dnfCount}
      </>
    );
  }

  const dnfIcons: JSX.Element[] = [];
  pushX(DNF_ICON, dnfCount, dnfIcons);
  return <>{dnfIcons}</>;
};

const getBestRows = (
  rosterOneDriverIds: string[],
  rosterTwoDriverIds: string[],
  driverStats: { [driverId: string]: DriverTrackStats },
  bestOverallTime: number | undefined,
  tournamentId: string
) => {
  rosterOneDriverIds.sort((a, b) =>
    timeAscending(driverStats[a].best, driverStats[b].best)
  );
  rosterTwoDriverIds.sort((a, b) =>
    timeAscending(driverStats[a].best, driverStats[b].best)
  );

  const maxNumDrivers = Math.max(
    rosterOneDriverIds.length,
    rosterTwoDriverIds.length
  );
  const bestRows: JSX.Element[] = [];
  for (let i = 0; i < maxNumDrivers; i++) {
    bestRows.push(
      getBestRow(
        rosterOneDriverIds.at(i),
        rosterTwoDriverIds.at(i),
        driverStats,
        bestOverallTime,
        tournamentId
      )
    );
  }

  return bestRows;
};

const getBestRow = (
  teamOneDriverId: string | undefined,
  teamTwoDriverId: string | undefined,
  driverStats: { [driverId: string]: DriverTrackStats },
  bestOverallTime: number | undefined,
  tournamentId: string
) => {
  const teamOneBest = !!teamOneDriverId
    ? driverStats[teamOneDriverId].best
    : undefined;

  const teamOneBestString = !!teamOneBest
    ? convertMsToTimeString(teamOneBest)
    : "";
  const teamOneBestDiff =
    !!teamOneBest && teamOneBest > 0 && !!bestOverallTime
      ? teamOneBest - bestOverallTime
      : 0;
  const teamOneBestDiffString =
    teamOneBestDiff !== 0 ? convertMsToSecondsString(teamOneBestDiff, "+") : "";

  const teamTwoBest = !!teamTwoDriverId
    ? driverStats[teamTwoDriverId].best
    : undefined;

  const teamTwoBestString = !!teamTwoBest
    ? convertMsToTimeString(teamTwoBest)
    : "";
  const teamTwoBestDiff =
    !!teamTwoBest && teamTwoBest > 0 && !!bestOverallTime
      ? teamTwoBest - bestOverallTime
      : 0;
  const teamTwoBestDiffString =
    teamTwoBestDiff !== 0 ? convertMsToSecondsString(teamTwoBestDiff, "+") : "";

  return (
    <tr>
      <PlayerCell textAlign="right">
        <DriverName driverId={teamOneDriverId} tournamentId={tournamentId} />
      </PlayerCell>
      <TimeDifferenceCell leftAlign={false}>
        <TimeDifferenceText time={teamOneBestDiff}>
          {teamOneBestDiffString}
        </TimeDifferenceText>
      </TimeDifferenceCell>
      <SetMatchTimeCell bold={teamOneBest === bestOverallTime}>
        {teamOneBestString}
      </SetMatchTimeCell>

      <TrackStatsCenterCell>
        <CenterText>Best</CenterText>
      </TrackStatsCenterCell>

      <SetMatchTimeCell bold={teamTwoBest === bestOverallTime}>
        {teamTwoBestString}
      </SetMatchTimeCell>
      <TimeDifferenceCell leftAlign={true}>
        <TimeDifferenceText time={teamTwoBestDiff}>
          {teamTwoBestDiffString}
        </TimeDifferenceText>
      </TimeDifferenceCell>
      <PlayerCell textAlign="left">
        <DriverName driverId={teamTwoDriverId} tournamentId={tournamentId} />
      </PlayerCell>
    </tr>
  );
};

interface DriverNameProps {
  driverId: string | undefined;
  tournamentId: string;
}

// todo - extract to be reusable
const DriverName = ({ driverId, tournamentId }: DriverNameProps) => {
  const driverName = useAppSelector((state) =>
    !!driverId
      ? selectTournamentDriverName(state, driverId, tournamentId)
      : undefined
  );

  return <span>{driverName ?? "???"}</span>;
};

const getMedianRows = (
  rosterOneDriverIds: string[],
  rosterTwoDriverIds: string[],
  driverStats: { [driverId: string]: DriverTrackStats },
  bestMedianTime: number | undefined,
  overallMedianTime: number | undefined,
  tournamentId: string
) => {
  rosterOneDriverIds.sort((a, b) =>
    timeAscending(driverStats[a].median, driverStats[b].median)
  );
  rosterTwoDriverIds.sort((a, b) =>
    timeAscending(driverStats[a].median, driverStats[b].median)
  );

  const maxNumDrivers = Math.max(
    rosterOneDriverIds.length,
    rosterTwoDriverIds.length
  );
  const medianRows: JSX.Element[] = [];
  for (let i = 0; i < maxNumDrivers; i++) {
    medianRows.push(
      getMedianRow(
        rosterOneDriverIds.at(i),
        rosterTwoDriverIds.at(i),
        driverStats,
        bestMedianTime,
        overallMedianTime,
        tournamentId
      )
    );
  }

  return medianRows;
};

const getMedianRow = (
  teamOneDriverId: string | undefined,
  teamTwoDriverId: string | undefined,
  driverStats: { [driverId: string]: DriverTrackStats },
  bestMedianTime: number | undefined,
  overallMedianTime: number | undefined,
  tournamentId: string
) => {
  const { dnfCount: teamOneDnfCount, median: teamOneMedian } = !!teamOneDriverId
    ? driverStats[teamOneDriverId]
    : { dnfCount: undefined, median: undefined };

  const teamOneMedianString = !!teamOneMedian
    ? convertMsToTimeString(teamOneMedian)
    : "";

  const teamOneMedianDiff =
    !!overallMedianTime && !!teamOneMedian && teamOneMedian > 0
      ? teamOneMedian - overallMedianTime
      : undefined;
  const teamOneMedianDiffString =
    teamOneMedianDiff !== undefined
      ? convertMsToSecondsString(
          Math.abs(teamOneMedianDiff),
          teamOneMedianDiff >= 0 ? "+" : "-"
        )
      : "";

  const { dnfCount: teamTwoDnfCount, median: teamTwoMedian } = !!teamTwoDriverId
    ? driverStats[teamTwoDriverId]
    : { dnfCount: undefined, median: undefined };

  const teamTwoMedianString = !!teamTwoMedian
    ? convertMsToTimeString(teamTwoMedian)
    : "";
  const teamTwoMedianDiff =
    !!overallMedianTime && !!teamTwoMedian && teamTwoMedian > 0
      ? teamTwoMedian - overallMedianTime
      : undefined;
  const teamTwoMedianDiffString =
    teamTwoMedianDiff !== undefined
      ? convertMsToSecondsString(
          Math.abs(teamTwoMedianDiff),
          teamTwoMedianDiff >= 0 ? "+" : "-"
        )
      : "";

  return (
    <tr>
      <PlayerCell textAlign="right">
        <DriverName driverId={teamOneDriverId} tournamentId={tournamentId} />
      </PlayerCell>
      <TimeDifferenceCell leftAlign={false}>
        <TimeDifferenceText time={teamOneMedianDiff ?? 0}>
          {teamOneMedianDiffString}
        </TimeDifferenceText>
      </TimeDifferenceCell>
      <SetMatchTimeCell
        bold={
          teamOneMedian !== undefined ? teamOneMedian === bestMedianTime : false
        }
      >
        {teamOneMedianString}
      </SetMatchTimeCell>
      <TrackStatsCenterCell>
        <FlexRow>
          <DnfContainer justify="right">
            {getDnfIcons(teamOneDnfCount ?? 0)}
          </DnfContainer>
          <CenterText>Median</CenterText>
          <DnfContainer justify="left">
            {getDnfIcons(teamTwoDnfCount ?? 0)}
          </DnfContainer>
        </FlexRow>
      </TrackStatsCenterCell>
      <SetMatchTimeCell
        bold={
          teamTwoMedian !== undefined ? teamTwoMedian === bestMedianTime : false
        }
      >
        {teamTwoMedianString}
      </SetMatchTimeCell>
      <TimeDifferenceCell leftAlign={true}>
        <TimeDifferenceText time={teamTwoMedianDiff ?? 0}>
          {teamTwoMedianDiffString}
        </TimeDifferenceText>
      </TimeDifferenceCell>
      <PlayerCell textAlign="left">
        <DriverName driverId={teamTwoDriverId} tournamentId={tournamentId} />
      </PlayerCell>
    </tr>
  );
};
